// Začátek směny — „Noční kokpit". Krok 1 výběr auta, krok 2 = Foto vozu
// z prototypu: dva velké sloty s náhledem (ťuknutí na hotový = přefocení),
// tachometr s mezerami po tisících, checklist vybavení, zamčené CTA
// s vypsaným důvodem. Logika (api.shift.start, upload fotek) beze změny.

import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { IonContent, IonPage, IonSpinner } from "@ionic/react";
import { Preferences } from "@capacitor/preferences";
import type { LoadoutItem, Vehicle } from "@routera/shared";
import { api, API_BASE_URL, loadToken } from "../lib/api";
import { capturePhoto, getCameraStream } from "../lib/camera";
import { useShiftStore } from "../store/shiftStore";
import { useGpsStore } from "../store/gpsStore";
import { useAuthStore } from "../store/authStore";
import { useMaterialStore } from "../store/materialStore";
import { ThemeToggle } from "../components/ThemeToggle";

function lastVehicleKey(driverId: string) {
  return `routera.last.vehicle.${driverId}`;
}

function vehiclePhotosKey(driverId: string) {
  const today = new Date().toISOString().slice(0, 10);
  return `routera.vehicle.photos.${driverId}.${today}`;
}

type Step = "vehicle" | "prep" | "submitting";
type Shot = { blob: Blob; filename: string; preview: string };

const FALLBACK_LOADOUT: LoadoutItem[] = [
  { label: "Mobilní telefon", checked: false },
  { label: "Klíče od vozidla", checked: false },
  { label: "Čip k vozidlu", checked: false },
  { label: "Tankovací karta", checked: false },
];

const CameraIcon = ({ size = 34 }: { size?: number }) => (
  <svg style={{ width: size, height: size }} viewBox="0 0 24 24">
    <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
    <circle cx="12" cy="14" r="3.4" />
  </svg>
);

/** „123456" → „123 456" (mezery po tisících dle délky čísla). */
const fmtOdo = (raw: string) =>
  raw.replace(/[^0-9]/g, "").slice(0, 7).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

export function ShiftStartScreen() {
  const history = useHistory();
  const location = useLocation();
  const rid = new URLSearchParams(location.search).get("rid");
  const setShift = useShiftStore((s) => s.setShift);
  const startGps = useGpsStore((s) => s.start);
  const driver = useAuthStore((s) => s.driver);
  const clearMaterial = useMaterialStore((s) => s.clear);
  const [step, setStep] = useState<Step>("vehicle");
  const [error, setError] = useState<string | null>(null);

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehicleId, setVehicleId] = useState<string>("");
  const [recommendedVehicleId, setRecommendedVehicleId] = useState<string | null>(null);
  const [showAllVehicles, setShowAllVehicles] = useState(false);
  const [skipPhotos, setSkipPhotos] = useState(false); // auto použito dnes → foto/km přeskočit

  const [photoFront, setPhotoFront] = useState<Shot | null>(null);
  const [photoBack, setPhotoBack] = useState<Shot | null>(null);

  const [odometer, setOdometer] = useState("");
  const [loadout, setLoadout] = useState<LoadoutItem[]>(FALLBACK_LOADOUT);
  const [routeName, setRouteName] = useState<string | null>(null);

  useEffect(() => {
    api.vehicles.list().then(
      (list) => setVehicles(list.filter((v) => v.active)),
      (e: any) => setError(e?.message ?? "Nepodařilo se načíst vozidla"),
    );
    void getCameraStream().catch(() => {});
    if (driver?.id) {
      Preferences.get({ key: lastVehicleKey(driver.id) }).then(({ value }) => {
        if (value) { setRecommendedVehicleId(value); setVehicleId(value); }
      });
    }
  }, [driver?.id]);

  useEffect(() => {
    api.route.today().then((routes) => {
      const route = rid ? (routes.find((r) => r.route_id === rid) ?? routes[0]) : routes[0];
      if (!route) return;
      setRouteName(route.name ?? null);
      const defaultLoadout = (route as any).default_loadout as LoadoutItem[] | undefined;
      if (Array.isArray(defaultLoadout) && defaultLoadout.length > 0) {
        setLoadout(defaultLoadout.map((it) => ({ ...it, checked: false })));
      }
      if (route.vehicle_id) {
        setRecommendedVehicleId(route.vehicle_id);
        setVehicleId(route.vehicle_id);
      }
    }).catch(() => {});
  }, [rid]);

  useEffect(() => {
    if (!vehicleId || !driver?.id) return;
    Preferences.get({ key: vehiclePhotosKey(driver.id) }).then(({ value }) => {
      setSkipPhotos(value === vehicleId);
    });
  }, [vehicleId, driver?.id]);

  const handleCapture = async (which: "front" | "back") => {
    setError(null);
    try {
      const photo = await capturePhoto();
      const shot: Shot = { ...photo, preview: URL.createObjectURL(photo.blob) };
      if (which === "front") setPhotoFront(shot);
      else setPhotoBack(shot);
    } catch (e: any) {
      setError(e?.message ?? "Foto se nepovedlo");
    }
  };

  const toggleItem = (idx: number) => {
    setLoadout((l) => l.map((it, i) => (i === idx ? { ...it, checked: !it.checked } : it)));
  };

  const allChecked = loadout.length > 0 && loadout.every((it) => it.checked);
  const odoOk = odometer.replace(/\s/g, "").length >= 3;
  const photosOk = skipPhotos || (!!photoFront && !!photoBack);
  const ready = photosOk && (skipPhotos || odoOk) && allChecked;
  const photoCount = (photoFront ? 1 : 0) + (photoBack ? 1 : 0);

  const whyLocked = !photosOk
    ? photoCount === 0 ? "Vyfoť vůz — předek i zadek" : "Chybí druhá fotka"
    : !skipPhotos && !odoOk
      ? "Zadej stav tachometru"
      : !allChecked
        ? "Odškrtej vybavení vozu"
        : "";

  const vehicle = vehicles.find((v) => v.id === vehicleId);

  const handleSubmit = async () => {
    setStep("submitting");
    setError(null);
    try {
      const odometerKm = odoOk ? parseInt(odometer.replace(/\s/g, ""), 10) : null;
      const targetRouteId = rid ?? null;
      const shift = await api.shift.start({
        vehicle_id: vehicleId || null,
        loadout_items: loadout,
        odometer_start: odometerKm,
        route_id: targetRouteId,
      });
      const token = await loadToken();
      if (!token) throw new Error("Token chybí");
      if (photoFront) {
        await api.shift.uploadPhoto(API_BASE_URL, token, shift.id, "pre_front", photoFront.blob, photoFront.filename);
      }
      if (photoBack) {
        await api.shift.uploadPhoto(API_BASE_URL, token, shift.id, "pre_back", photoBack.blob, photoBack.filename);
      }
      clearMaterial();
      setShift(shift);
      if (driver?.id) {
        void startGps(shift.id, driver.id);
        if (vehicleId) {
          void Preferences.set({ key: lastVehicleKey(driver.id), value: vehicleId });
          if (photoFront || photoBack) {
            void Preferences.set({ key: vehiclePhotosKey(driver.id), value: vehicleId });
          }
        }
      }
      history.replace(targetRouteId ? `/route?rid=${targetRouteId}` : "/");
    } catch (e: any) {
      setError(e?.message ?? "Nepodařilo se začít směnu");
      setStep("prep");
    }
  };

  const vehicleRow = (v: Vehicle) => (
    <button
      key={v.id}
      className="actrow"
      onClick={() => setVehicleId(v.id)}
      style={vehicleId === v.id ? { borderColor: "var(--brass)", background: "rgba(227,177,92,.10)" } : undefined}
    >
      <span
        className="aic"
        style={{
          borderRadius: "50%", width: 22, height: 22,
          border: `2px solid ${vehicleId === v.id ? "var(--brass)" : "var(--dim)"}`,
          background: vehicleId === v.id ? "var(--brass)" : "transparent",
        }}
      />
      <span style={{ minWidth: 0 }}>
        <b>{v.name}</b>
        <small className="mono">SPZ {v.spz}</small>
      </span>
    </button>
  );

  const recommended = recommendedVehicleId ? vehicles.find((v) => v.id === recommendedVehicleId) : null;
  const others = vehicles.filter((v) => v.id !== recommendedVehicleId);

  return (
    <IonPage>
      <IonContent scrollY={false}>
        <div className="ck scenebg" style={{ height: "100%" }}>
          <div className="top">
            <button className="back" onClick={() => (step === "prep" ? setStep("vehicle") : history.replace("/"))}>‹</button>
            <span className="wm">ROUTERA <em>DRIVER</em></span>
            <span className="live" style={{ color: "var(--dim)" }}>
              <span className="dot" style={{ background: "var(--dim)", animation: "none" }} />
              {routeName ?? "směna"}
            </span>
            <ThemeToggle />
          </div>

          <div className="stopbody"><div className="pad-x">
            {error && (
              <div className="card" role="alert" style={{ marginTop: 14, borderColor: "rgba(242,101,88,.5)", background: "var(--bad-dim)" }}>
                <b style={{ color: "var(--bad)", fontSize: 14 }}>{error}</b>
              </div>
            )}

            {step === "vehicle" && (
              <>
                <h1>Jaké máš dnes auto?</h1>
                <div className="h-sub">{routeName ? `Trasa ${routeName}` : "Výběr vozidla"}</div>

                {vehicles.length === 0 ? (
                  <div style={{ padding: "26px 0", color: "var(--dim)", textAlign: "center" }}>Načítám vozidla…</div>
                ) : (
                  <div style={{ marginTop: 8 }}>
                    {recommended && (
                      <>
                        <div className="cap" style={{ marginTop: 10, marginBottom: 0 }}>Tvoje auto</div>
                        {vehicleRow(recommended)}
                      </>
                    )}
                    {others.length > 0 && (
                      <>
                        {recommended ? (
                          <button className="ghostbtn" style={{ marginTop: 10 }} onClick={() => setShowAllVehicles((v) => !v)}>
                            {showAllVehicles ? "Skrýt ostatní vozidla" : `Jiné auto (${others.length})`}
                          </button>
                        ) : (
                          <div className="cap" style={{ marginTop: 10, marginBottom: 0 }}>Vozidla</div>
                        )}
                        {(!recommended || showAllVehicles) && others.map(vehicleRow)}
                      </>
                    )}
                  </div>
                )}
                {skipPhotos && vehicleId && (
                  <div style={{ fontSize: 12.5, color: "var(--ok)", marginTop: 12, textAlign: "center", fontWeight: 600 }}>
                    Toto auto jsi dnes už fotil — fotky a km přeskočíme
                  </div>
                )}
              </>
            )}

            {step === "prep" && (
              <>
                <h1>
                  Foto vozu{" "}
                  {!skipPhotos && (
                    <span className="mono" style={{ color: "var(--brass)", fontSize: 16, verticalAlign: 2 }}>
                      {photoCount} ze 2
                    </span>
                  )}
                </h1>
                <div className="h-sub">{vehicle ? `${vehicle.spz} · ${vehicle.name}` : routeName ?? ""}</div>

                <div style={{ marginTop: 16 }}>
                  {!skipPhotos && (
                    <>
                      <button className={`photoslot${photoFront ? " shot" : ""}`} onClick={() => void handleCapture("front")}>
                        {photoFront && <img src={photoFront.preview} alt="" />}
                        <CameraIcon />
                        <span>Předek + bok{photoFront ? " ✓" : ""}</span>
                      </button>
                      <button className={`photoslot${photoBack ? " shot" : ""}`} onClick={() => void handleCapture("back")}>
                        {photoBack && <img src={photoBack.preview} alt="" />}
                        <CameraIcon />
                        <span>Zadek + bok{photoBack ? " ✓" : ""}</span>
                      </button>

                      <div style={{ marginTop: 14 }}>
                        <div className="cap" style={{ marginBottom: 8 }}>Stav tachometru</div>
                        <div style={{ position: "relative" }}>
                          <input
                            className="wsfield"
                            style={{ margin: 0, textAlign: "left", letterSpacing: ".06em", paddingRight: 58 }}
                            inputMode="numeric"
                            placeholder="0"
                            value={odometer}
                            onChange={(e) => setOdometer(fmtOdo(e.target.value))}
                          />
                          <span style={{
                            position: "absolute", right: 18, top: "50%", transform: "translateY(-50%)",
                            color: "var(--dim)", fontWeight: 700, fontSize: 14,
                          }}>km</span>
                        </div>
                      </div>
                    </>
                  )}

                  <div style={{ marginTop: 14 }}>
                    <div className="cap" style={{ marginBottom: 8 }}>Vybavení vozu</div>
                    {loadout.map((it, idx) => (
                      <div className={`srvrow${it.checked ? " on" : ""}`} key={idx}>
                        <button className="srvchk" onClick={() => toggleItem(idx)}>
                          <span className="bx">✓</span> {it.label}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {step === "submitting" && (
              <div style={{ textAlign: "center", padding: 60 }}>
                <IonSpinner name="crescent" />
                <p style={{ color: "var(--dim)", marginTop: 12 }}>Zakládám směnu a nahrávám fotky…</p>
              </div>
            )}
          </div></div>

          <div className="bottom">
            {step === "vehicle" && (
              <button
                className={`cta${!vehicleId ? " locked" : ""}`}
                disabled={!vehicleId}
                onClick={() => setStep("prep")}
              >
                Pokračovat
              </button>
            )}
            {step === "prep" && (
              <>
                <button
                  className={`cta${!ready ? " locked" : ""}`}
                  disabled={!ready}
                  onClick={() => void handleSubmit()}
                >
                  Vyrazit na trasu
                </button>
                <div className="foto-why">{whyLocked}</div>
              </>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
